import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ChatMessage from './ChatMessage';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@heroui/react';
function ChatContainer({ messages, isLoading = false, onCopy, loadingMessage, fontSize = 'medium', fontSizeValue: initialFontSize = 14 }) {
    const scrollRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const previousMessageIdsRef = useRef(new Set());
    const [currentFontSize, setCurrentFontSize] = useState(initialFontSize);
    const messagesWithAnimationMeta = useMemo(()=>{
        const previousIds = previousMessageIdsRef.current;
        return messages.map((message)=>({
                data: message,
                isNew: !previousIds.has(message.id)
            }));
    }, [
        messages
    ]);
    useEffect(()=>{
        previousMessageIdsRef.current = new Set(messages.map((message)=>message.id));
    }, [
        messages
    ]);
    // Listen for font size changes
    useEffect(()=>{
        const handleFontSizeChange = (event)=>{
            setCurrentFontSize(event.detail);
        };
        window.addEventListener('ai-chat-font-size-changed', handleFontSizeChange);
        // Also check localStorage on mount
        const savedSize = localStorage.getItem('ai-chat-font-size-value');
        if (savedSize) {
            setCurrentFontSize(parseInt(savedSize));
        }
        return ()=>{
            window.removeEventListener('ai-chat-font-size-changed', handleFontSizeChange);
        };
    }, []);
    // Auto-scroll to bottom when new messages arrive
    useEffect(()=>{
        if (isAtBottom && scrollRef.current) {
            scrollToBottom();
        }
    }, [
        messages,
        isAtBottom
    ]);
    const scrollToBottom = ()=>{
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };
    const handleScroll = ()=>{
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setIsAtBottom(isNearBottom);
        setShowScrollButton(!isNearBottom && messages.length > 0);
    };
    const handleCopy = (content)=>{
        onCopy?.(content);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative w-full overflow-hidden",
        "data-testid": "ai-chat-messages",
        style: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: '1 1 0%',
            overflow: 'hidden'
        },
        children: [
            /*#__PURE__*/ _jsx("style", {
                children: `
        /* Force no horizontal scroll everywhere */
        * {
          max-width: 100% !important;
        }

        .chat-container-scroll {
          scrollbar-width: thin;
          scrollbar-gutter: stable;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          max-width: 100% !important;
          width: 100% !important;
        }

        .chat-container-scroll * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }

        /* Hide horizontal scrollbar completely */
        .chat-container-scroll::-webkit-scrollbar {
          width: 6px;
          height: 0 !important;
        }

        .chat-container-scroll::-webkit-scrollbar:horizontal {
          display: none !important;
        }

        .chat-container-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-container-scroll::-webkit-scrollbar-thumb {
          background: #9CA3AF;
          border-radius: 3px;
        }

        .chat-container-scroll::-webkit-scrollbar-thumb:hover {
          background: #6B7280;
        }

        .dark .chat-container-scroll::-webkit-scrollbar-thumb {
          background: #6B7280;
        }

        .dark .chat-container-scroll::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }

        /* Ensure no horizontal overflow on any child */
        .prose {
          max-width: 100% !important;
        }

        .prose * {
          max-width: 100% !important;
          word-break: break-word !important;
        }

        pre {
          max-width: 100% !important;
          overflow-x: auto !important;
        }

        table {
          display: block !important;
          max-width: 100% !important;
          overflow-x: auto !important;
        }
      `
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: scrollRef,
                onScroll: handleScroll,
                className: "chat-container-scroll",
                style: {
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#9CA3AF transparent',
                    flex: '1 1 0%',
                    minHeight: 0,
                    height: 'auto',
                    overflow: 'hidden auto'
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mx-auto w-full space-y-4 px-3 py-4",
                    style: {
                        maxWidth: '100%',
                        overflow: 'hidden',
                        fontSize: `${currentFontSize}px`
                    },
                    children: [
                        messagesWithAnimationMeta.map(({ data: message, isNew })=>{
                            const isLastMessage = messages[messages.length - 1]?.id === message.id;
                            return /*#__PURE__*/ _jsx(motion.div, {
                                initial: isNew ? {
                                    opacity: 0,
                                    y: 12
                                } : false,
                                animate: {
                                    opacity: 1,
                                    y: 0
                                },
                                transition: {
                                    duration: 0.25,
                                    ease: 'easeOut'
                                },
                                children: /*#__PURE__*/ _jsx(ChatMessage, {
                                    id: message.id,
                                    content: message.content,
                                    role: message.role,
                                    timestamp: message.timestamp,
                                    isLast: isLastMessage,
                                    isLoading: isLoading && message.role === 'assistant' && (!message.content || message.content.trim() === '') && isLastMessage,
                                    onCopy: ()=>handleCopy(message.content),
                                    fontSizeValue: currentFontSize
                                })
                            }, message.id);
                        }),
                        (()=>{
                            const last = messages[messages.length - 1];
                            const hasAssistantBubble = !!last && last.role === 'assistant';
                            if (!isLoading || hasAssistantBubble) {
                                return null;
                            }
                            return /*#__PURE__*/ _jsx(motion.div, {
                                initial: {
                                    opacity: 0,
                                    y: 12
                                },
                                animate: {
                                    opacity: 1,
                                    y: 0
                                },
                                transition: {
                                    duration: 0.25,
                                    ease: 'easeOut'
                                },
                                children: /*#__PURE__*/ _jsx(ChatMessage, {
                                    id: "loading",
                                    content: loadingMessage || '',
                                    role: "assistant",
                                    timestamp: new Date(),
                                    isLoading: true,
                                    fontSizeValue: currentFontSize
                                })
                            }, "loading-bubble");
                        })()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(AnimatePresence, {
                children: showScrollButton && /*#__PURE__*/ _jsx(motion.div, {
                    initial: {
                        scale: 0,
                        opacity: 0
                    },
                    animate: {
                        scale: 1,
                        opacity: 1
                    },
                    exit: {
                        scale: 0,
                        opacity: 0
                    },
                    className: "absolute bottom-4 right-4 z-10",
                    children: /*#__PURE__*/ _jsx(Button, {
                        isIconOnly: true,
                        size: "lg",
                        radius: "full",
                        color: "primary",
                        variant: "shadow",
                        onClick: scrollToBottom,
                        className: "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl transition-all",
                        children: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                            className: "size-5"
                        })
                    })
                })
            })
        ]
    });
}
export default ChatContainer;
